/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Box,
    Text,

    // Hooks
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Icons
import {
    CheckIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    FlagDEIcon,
    FlagFRIcon,
    FlagITIcon,
    FlagATIcon,
    FlagBEIcon,
    FlagBGIcon,
    FlagCHIcon,
    FlagCZIcon,
    FlagDKIcon,
    FlagESIcon,
    FlagFIIcon,
    FlagHRIcon,
    FlagHUIcon,
    FlagIEIcon,
    FlagLTIcon,
    FlagLUIcon,
    FlagLVIcon,
    FlagNLIcon,
    FlagPLIcon,
    FlagPTIcon,
    FlagROIcon,
    FlagSEIcon,
    FlagSIIcon,
    FlagSKIcon,
    FlagMTIcon,
    FlagGRIcon,
    FlagEEIcon,
    FlagCYIcon,
    FlagNOIcon,
    FlagLIIcon
} from '../icons'

const languageNames = new Intl.DisplayNames(['en'], {type: 'language'})

const formatLocaleLabel = (locale) => {
    const [languageCode, countryCode] = locale.split('-')
    return `${countryCode.toUpperCase()}-${languageNames.of(languageCode)}`
}

// NOTE: If you want to have flags shown next to a selectable locale, update this
// mapping object with the short code as the key for the desired icon.
const flags = {
    'de-DE': <FlagDEIcon />,
    'en-DE': <FlagDEIcon />,
    'en-FR': <FlagFRIcon />,
    'en-IT': <FlagITIcon />,
    'en-AT': <FlagATIcon />,
    'en-BE': <FlagBEIcon />,
    'en-BG': <FlagBGIcon />,
    'en-CH': <FlagCHIcon />,
    'en-CZ': <FlagCZIcon />,
    'en-DK': <FlagDKIcon />,
    'en-ES': <FlagESIcon />,
    'en-FI': <FlagFIIcon />,
    'en-HR': <FlagHRIcon />,
    'en-HU': <FlagHUIcon />,
    'en-IE': <FlagIEIcon />,
    'en-LT': <FlagLTIcon />,
    'en-LU': <FlagLUIcon />,
    'en-LV': <FlagLVIcon />,
    'en-NL': <FlagNLIcon />,
    'en-PL': <FlagPLIcon />,
    'en-PT': <FlagPTIcon />,
    'en-RO': <FlagROIcon />,
    'en-SE': <FlagSEIcon />,
    'en-SI': <FlagSIIcon />,
    'en-SK': <FlagSKIcon />,
    'en-MT': <FlagMTIcon />,
    'en-GR': <FlagGRIcon />,
    'en-EE': <FlagEEIcon />,
    'en-CY': <FlagCYIcon />,
    'en-NO': <FlagNOIcon />,
    'en-LI': <FlagLIIcon />
}

/**
 * The Locale Selector is a disclosure in the form of an accordion. It is
 * populated with all the supported locales for the application allowing the
 * user to change the current locale.
 */
const LocaleSelector = ({selectedLocale = '', locales = [], onSelect = () => {}, ...props}) => {
    const styles = useStyleConfig('LocaleSelector')
    return (
        <Box className="sf-locale-selector">
            <Accordion allowToggle={true} {...props}>
                <AccordionItem border="none">
                    {({isExpanded}) => (
                        <>
                            <AccordionButton {...styles.selectedButton}>
                                {/* Replace default expanded/collapsed icons. */}
                                {isExpanded ? (
                                    <ChevronDownIcon {...styles.selectedButtonIcon} />
                                ) : (
                                    <ChevronRightIcon {...styles.selectedButtonIcon} />
                                )}
                                {/* Display flag icon if one exists */}
                                {flags[selectedLocale]}
                                <Text {...styles.selectedText}>
                                    {formatLocaleLabel(selectedLocale)}
                                </Text>
                            </AccordionButton>
                            <AccordionPanel>
                                <Accordion
                                    allowToggle={true}
                                    {...styles.accordion}
                                    maxH="300px"
                                    overflowY="auto"
                                >
                                    {locales.map((locale) => {
                                        return (
                                            <AccordionItem border="none" key={locale}>
                                                <AccordionButton
                                                    {...styles.optionButton}
                                                    onClick={() => onSelect(locale)}
                                                >
                                                    {/* Display flag icon if one exists */}
                                                    {flags[locale]}

                                                    {/* Locale name */}
                                                    <Text {...styles.optionText}>
                                                        {formatLocaleLabel(locale)}
                                                    </Text>

                                                    {/* Selection indicator */}
                                                    {selectedLocale === locale && (
                                                        <CheckIcon {...styles.selectedIcon} />
                                                    )}
                                                </AccordionButton>
                                            </AccordionItem>
                                        )
                                    })}
                                </Accordion>
                            </AccordionPanel>
                        </>
                    )}
                </AccordionItem>
            </Accordion>
        </Box>
    )
}

LocaleSelector.displayName = 'LocaleSelector'

LocaleSelector.propTypes = {
    /**
     * A complete list of all the locales supported. This array must have content.
     */
    locales: PropTypes.arrayOf(PropTypes.string).isRequired,
    /**
     * The current locales shortcode.
     */
    selectedLocale: PropTypes.string.isRequired,
    /**
     * Function called when a locale is selected.
     */
    onSelect: PropTypes.func
}

export default LocaleSelector
